<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div class="w-full max-w-sm">
      <!-- Minimal header -->
      <div class="text-center mb-8">
        <h1 class="text-2xl font-normal text-gray-900 mb-1">Gotham Time</h1>
        <p class="text-sm text-gray-500">Sign in to your account</p>
      </div>

      <!-- Material Design Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Email Field -->
          <div class="space-y-2">
            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
            <input
              id="email"
              type="email"
              v-model="email"
              placeholder="Enter your email"
              required
              :class="[
                'w-full px-3 py-2 border rounded-md text-sm transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                emailError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              ]"
              @blur="validateEmail"
            />
            <p v-if="emailError" class="text-xs text-red-600">{{ emailError }}</p>
          </div>
          
          <!-- Password Field -->
          <div class="space-y-2">
            <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
            <div class="relative">
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="Enter your password"
                required
                :class="[
                  'w-full px-3 py-2 pr-10 border rounded-md text-sm transition-colors duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  passwordError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                ]"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <Eye v-if="!showPassword" class="h-4 w-4" />
                <EyeOff v-else class="h-4 w-4" />
              </button>
            </div>
            <p v-if="passwordError" class="text-xs text-red-600">{{ passwordError }}</p>
          </div>

          <!-- Error Alert -->
          <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-3">
            <p class="text-sm text-red-800">{{ error }}</p>
          </div>

          <!-- Sign In Button -->
          <button 
            type="submit" 
            :disabled="isLoading || !isFormValid"
            :class="[
              'w-full py-2 px-4 rounded-md text-sm font-medium transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              isLoading || !isFormValid 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
            ]"
          >
            <span v-if="isLoading" class="flex items-center justify-center">
              <div class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Signing in...
            </span>
            <span v-else>Sign in</span>
          </button>
        </form>
      </div>

      <!-- Footer -->
      <div class="text-center mt-6">
        <p class="text-xs text-gray-500">
          Secure login powered by Gotham Time
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Eye, EyeOff } from 'lucide-vue-next'

export default {
  name: 'LoginPage',
  components: {
    Eye,
    EyeOff
  },
  setup() {
    const auth = inject('auth')
    const router = useRouter()
    const email = ref('')
    const password = ref('')
    const error = ref('')
    const isLoading = ref(false)
    const showPassword = ref(false)
    const emailError = ref('')
    const passwordError = ref('')

    const isFormValid = computed(() => {
      return email.value && password.value && !emailError.value
    })

    const validateEmail = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!email.value) {
        emailError.value = 'Email is required'
      } else if (!emailRegex.test(email.value)) {
        emailError.value = 'Please enter a valid email address'
      } else {
        emailError.value = ''
      }
    }

    const validatePassword = () => {
      if (!password.value) {
        passwordError.value = 'Password is required'
      } else if (password.value.length > 8) {
        passwordError.value = 'Password must be at most 8 characters'
      } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password.value)) {
        passwordError.value = 'Password must contain at least one special character'
      } else {
        passwordError.value = ''
      }
    }

    const navigateToDashboard = (user) => {
      // All users now go to /dashboard - AppContent handles role-based component rendering
      router.push('/dashboard')
    }

    const handleSubmit = async () => {
      validateEmail()
      
      if (!email.value || emailError.value) {
        return
      }

      isLoading.value = true
      error.value = ''
      passwordError.value = ''

      try {
        const success = await auth.login(email.value, password.value)
        
        if (success) {
          // Navigate to appropriate dashboard after successful login
          navigateToDashboard(auth.user.value)
        } else {
          error.value = 'Invalid credentials. Please try again.'
        }
      } catch (err) {
        error.value = 'Login failed. Please try again.'
      } finally {
        isLoading.value = false
      }
    }

    return {
      email,
      password,
      error,
      isLoading,
      showPassword,
      emailError,
      passwordError,
      isFormValid,
      validateEmail,
      validatePassword,
      handleSubmit
    }
  }
}
</script>